import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  BarChart3, TrendingUp, Users, Shield, Activity,
  AlertTriangle, Clock, CheckCircle, FileText, FolderOpen,
  ClipboardList, Award, Eye, Download, CalendarRange, ArrowUpRight, ArrowDownRight,
  Trophy
} from 'lucide-react';
import api, { downloadBlob } from '../services/api';
import { shortenDeptName, getDateLocale } from '../utils/i18nHelpers';
import './AnalyticsDashboard.css';

const TAB_ICONS = {
  overview: BarChart3,
  kpi: Award,
  trends: TrendingUp,
  activity: Activity,
  discipline: Shield,
  year_comparison: CalendarRange,
  risk_map: AlertTriangle,
  rating: Trophy,
};

const TAB_KEYS = ['overview', 'kpi', 'trends', 'activity', 'discipline', 'year_comparison', 'risk_map', 'rating'];

const COLORS = ['#1e88e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function AnalyticsDashboard() {
  const { t } = useTranslation(['analytics', 'common']);
  const navigate = useNavigate();

  const months = t('common:months', { returnObjects: true });
  const formatMonth = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return months[d.getMonth()] || '';
  };

  const ACTION_LABELS = {
    create: t('common:actions.create'),
    update: t('common:actions.update'),
    delete: t('common:actions.delete'),
    view: t('common:actions.view'),
    login: t('common:actions.login'),
    logout: t('common:actions.logout'),
  };

  const TABS = TAB_KEYS.map(key => ({
    key,
    label: t(`analytics:tabs.${key === 'year_comparison' ? 'yearComparison' : key === 'risk_map' ? 'riskMap' : key}`),
    icon: TAB_ICONS[key],
  }));
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [days, setDays] = useState(30);

  // Данные для каждой вкладки
  const [dashboard, setDashboard] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [disciplineData, setDisciplineData] = useState(null);
  const [yearCompData, setYearCompData] = useState(null);
  const [yearCompYear1, setYearCompYear1] = useState(new Date().getFullYear() - 1);
  const [yearCompYear2, setYearCompYear2] = useState(new Date().getFullYear());
  const [riskMapData, setRiskMapData] = useState(null);
  const [selectedRiskDept, setSelectedRiskDept] = useState(null);
  const [ratingTableData, setRatingTableData] = useState(null);
  const [ratingHistory, setRatingHistory] = useState(null);
  const [ratingDeptId, setRatingDeptId] = useState(null);
  const [ratingPeriodType, setRatingPeriodType] = useState('weekly');
  const [generatingSnapshots, setGeneratingSnapshots] = useState(false);

  const loadTabData = useCallback(async (tab) => {
    setLoading(true);
    try {
      switch (tab) {
        case 'overview': {
          const res = await api.getDashboardAnalytics({ year });
          setDashboard(res.data);
          break;
        }
        case 'kpi': {
          const res = await api.getDepartmentKPI({ year });
          setKpiData(res.data);
          break;
        }
        case 'trends': {
          const res = await api.getTrends({ year });
          setTrendsData(res.data);
          break;
        }
        case 'activity': {
          const res = await api.getUserActivity({ days });
          setActivityData(res.data);
          break;
        }
        case 'discipline': {
          const res = await api.getExecutionDiscipline({ year });
          setDisciplineData(res.data);
          break;
        }
        case 'year_comparison': {
          const res = await api.getYearComparison({ year1: yearCompYear1, year2: yearCompYear2 });
          setYearCompData(res.data);
          break;
        }
        case 'risk_map': {
          const res = await api.getRiskMap({ year });
          setRiskMapData(res.data);
          setSelectedRiskDept(null);
          break;
        }
        case 'rating': {
          const res = await api.getRatingTable({ period_type: ratingPeriodType, year });
          setRatingTableData(res.data);
          setRatingHistory(null);
          setRatingDeptId(null);
          break;
        }
        default: break;
      }
    } catch (error) {
      console.error(`Ошибка загрузки ${tab}:`, error);
    }
    setLoading(false);
  }, [year, days, yearCompYear1, yearCompYear2, ratingPeriodType]);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // ─── Вкладка: Обзор ────────────────────────────────────────────────

  const renderOverview = () => {
    if (!dashboard) return null;
    const { reports, documents, plans, users, recent_actions, alerts } = dashboard;

    return (
      <div className="tab-content">
        {/* Карточки */}
        <div className="stats-grid">
          <div className="stat-card blue">
            <FileText size={24} />
            <div>
              <div className="stat-value">{reports.total}</div>
              <div className="stat-label">{t('analytics:overview.reports')} ({t('analytics:overview.reportsApproved', { rate: reports.approval_rate })})</div>
            </div>
          </div>
          <div className="stat-card orange">
            <FolderOpen size={24} />
            <div>
              <div className="stat-value">{documents.total}</div>
              <div className="stat-label">{t('analytics:overview.documents')} ({t('analytics:overview.documentsOverdue', { count: documents.overdue })})</div>
            </div>
          </div>
          <div className="stat-card green">
            <ClipboardList size={24} />
            <div>
              <div className="stat-value">{plans.total}</div>
              <div className="stat-label">{t('analytics:overview.plans')} ({t('analytics:overview.plansCompletion', { rate: plans.avg_completion })})</div>
            </div>
          </div>
          <div className="stat-card purple">
            <Users size={24} />
            <div>
              <div className="stat-value">{users.total}</div>
              <div className="stat-label">{t('analytics:overview.users')} ({t('analytics:overview.usersToday', { count: users.active_today })})</div>
            </div>
          </div>
        </div>

        {/* Алерты */}
        {(alerts.overdue_documents.length > 0 || alerts.upcoming_deadlines.length > 0 || alerts.overdue_plan_tasks.length > 0) && (
          <div className="alerts-section">
            <h3><AlertTriangle size={18} /> {t('analytics:overview.requiresAttention')}</h3>
            <div className="alerts-grid">
              {alerts.overdue_documents.length > 0 && (
                <div className="alert-card danger">
                  <h4>{t('analytics:overview.overdueDocuments')} ({alerts.overdue_documents.length})</h4>
                  {alerts.overdue_documents.map(doc => (
                    <div key={doc.id} className="alert-item" onClick={() => navigate(`/documents/${doc.id}`)}>
                      <span className="alert-title">{doc.registration_number} — {doc.title}</span>
                      <span className="alert-meta">{t('analytics:overview.overdueByDays', { days: doc.days_overdue })} | {doc.department_name}</span>
                    </div>
                  ))}
                </div>
              )}
              {alerts.upcoming_deadlines.length > 0 && (
                <div className="alert-card warning">
                  <h4>{t('analytics:overview.upcomingDeadlines')} ({alerts.upcoming_deadlines.length})</h4>
                  {alerts.upcoming_deadlines.map(doc => (
                    <div key={doc.id} className="alert-item" onClick={() => navigate(`/documents/${doc.id}`)}>
                      <span className="alert-title">{doc.registration_number} — {doc.title}</span>
                      <span className="alert-meta">{t('analytics:overview.daysRemaining', { days: doc.days_remaining })} | {doc.department_name}</span>
                    </div>
                  ))}
                </div>
              )}
              {alerts.overdue_plan_tasks.length > 0 && (
                <div className="alert-card danger">
                  <h4>{t('analytics:overview.overdueTasks')} ({alerts.overdue_plan_tasks.length})</h4>
                  {alerts.overdue_plan_tasks.map(task => (
                    <div key={task.id} className="alert-item">
                      <span className="alert-title">{task.title}</span>
                      <span className="alert-meta">{t('analytics:overview.overdueByDays', { days: task.days_overdue })} | {task.department_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Последние действия */}
        {recent_actions.length > 0 && (
          <div className="section-card">
            <h3><Eye size={18} /> {t('analytics:overview.lastActions')}</h3>
            <div className="actions-list">
              {recent_actions.map((action, i) => (
                <div key={i} className="action-row">
                  <span className="action-user">{action.user_name}</span>
                  <span className={`action-type type-${action.action}`}>
                    {action.action_display}
                  </span>
                  <span className="action-desc">{action.model_name}{action.description ? ` — ${action.description}` : ''}</span>
                  <span className="action-time">{new Date(action.created_at).toLocaleString(getDateLocale())}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Аналитическая справка — экспорт */}
        <div className="section-card">
          <h3><Download size={18} /> {t('analytics:overview.analyticalReport')}</h3>
          <p className="export-hint">{t('analytics:overview.analyticalReportHint')}</p>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button className="btn-export" onClick={async () => {
              try { const r = await api.exportAnalyticalWord({ year }); downloadBlob(r, `${t('analytics:export.analyticalFilename')}_${year}.docx`); } catch {}
            }}><Download size={14} /> Word</button>
            <button className="btn-export" onClick={async () => {
              try { const r = await api.exportAnalyticalPdf({ year }); downloadBlob(r, `${t('analytics:export.analyticalFilename')}_${year}.pdf`); } catch {}
            }}><Download size={14} /> PDF</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Вкладка: KPI отделов ──────────────────────────────────────────

  const renderKPI = () => {
    if (!kpiData) return null;
    const { departments } = kpiData;
    const top10 = departments.slice(0, 10);

    return (
      <div className="tab-content">
        <div className="filter-bar">
          <label>{t('analytics:common.year')}:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            <button className="btn-export" onClick={async () => {
              try { const r = await api.exportKpiExcel({ year }); downloadBlob(r, `KPI_${year}.xlsx`); } catch {}
            }}><Download size={14} /> {t('analytics:export.excel')}</button>
            <button className="btn-export" onClick={async () => {
              try { const r = await api.exportKpiPdf({ year }); downloadBlob(r, `KPI_${year}.pdf`); } catch {}
            }}><Download size={14} /> {t('analytics:export.pdf')}</button>
          </div>
        </div>

        {/* BarChart TOP 10 */}
        <div className="chart-card">
          <h3><Award size={18} /> {t('analytics:kpi.departmentRating')} ({t('analytics:kpi.top10')})</h3>
          {top10.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={top10} layout="vertical" margin={{ left: 200 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis type="category" dataKey="department_name" width={190} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `${v} ${t('analytics:kpi.points')}`} />
                <Bar dataKey="kpi_score" fill="#7c3aed" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data">{t('analytics:kpi.noDataForYear', { year })}</div>
          )}
        </div>

        {/* Таблица */}
        <div className="section-card">
          <h3>{t('analytics:kpi.detailedRating')}</h3>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('common:table.department')}</th>
                  <th>KPI</th>
                  <th>{t('analytics:kpi.reportsApproved')}</th>
                  <th>{t('analytics:kpi.documentsOnTime')}</th>
                  <th>{t('analytics:kpi.plansCompleted')}</th>
                  <th>{t('analytics:kpi.overdue')}</th>
                </tr>
              </thead>
              <tbody>
                {departments.map(dept => (
                  <tr key={dept.department_id}>
                    <td className="rank-cell">
                      <span className={`rank-badge ${dept.rank <= 3 ? 'top' : ''}`}>{dept.rank}</span>
                    </td>
                    <td>{dept.department_name}</td>
                    <td>
                      <div className="kpi-cell">
                        <div className="progress-bar-mini">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${Math.min(dept.kpi_score, 100)}%`,
                              background: dept.kpi_score >= 70 ? '#10b981' : dept.kpi_score >= 40 ? '#f59e0b' : '#ef4444'
                            }}
                          />
                        </div>
                        <span>{dept.kpi_score}</span>
                      </div>
                    </td>
                    <td>{dept.reports.total} ({dept.reports.approval_rate}%)</td>
                    <td>{dept.documents.total_executions} ({dept.documents.on_time_rate}%)</td>
                    <td>{dept.plans.total} ({dept.plans.avg_completion}%)</td>
                    <td>
                      {dept.overdue_tasks > 0
                        ? <span className="overdue-badge">{dept.overdue_tasks}</span>
                        : <span className="ok-badge">0</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── Вкладка: Тренды ───────────────────────────────────────────────

  const renderTrends = () => {
    if (!trendsData) return null;
    const { reports_trend, documents_trend, tasks_trend } = trendsData;

    const fmtReports = reports_trend.map(r => ({ ...r, month: formatMonth(r.month) }));
    const fmtDocs = documents_trend.map(r => ({ ...r, month: formatMonth(r.month) }));
    const fmtTasks = tasks_trend.map(r => ({ ...r, month: formatMonth(r.month) }));

    return (
      <div className="tab-content">
        <div className="filter-bar">
          <label>{t('analytics:common.year')}:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div className="charts-grid">
          <div className="chart-card">
            <h3><FileText size={18} /> {t('analytics:trends.reportsCreated')}</h3>
            {fmtReports.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={fmtReports}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#1e88e5" strokeWidth={2} name={t('analytics:trends.total')} />
                  <Line type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={2} name={t('analytics:trends.approved')} />
                  <Line type="monotone" dataKey="rejected" stroke="#ef4444" strokeWidth={2} name={t('analytics:trends.rejected')} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="no-data">{t('common:noData')}</div>}
          </div>

          <div className="chart-card">
            <h3><FolderOpen size={18} /> {t('analytics:trends.documentsRegistered')}</h3>
            {fmtDocs.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={fmtDocs}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#1e88e5" strokeWidth={2} name={t('analytics:trends.total')} />
                  <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} name={t('analytics:trends.completed')} />
                  <Line type="monotone" dataKey="overdue" stroke="#ef4444" strokeWidth={2} name={t('analytics:trends.overdue')} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="no-data">{t('common:noData')}</div>}
          </div>
        </div>

        <div className="chart-card" style={{ marginTop: '1.5rem' }}>
          <h3><ClipboardList size={18} /> {t('analytics:trends.plansCreated')}</h3>
          {fmtTasks.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={fmtTasks}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="total" stroke="#1e88e5" strokeWidth={2} name={t('analytics:trends.total')} />
                <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} name={t('analytics:trends.done')} />
                <Line type="monotone" dataKey="overdue" stroke="#ef4444" strokeWidth={2} name={t('analytics:trends.overdue')} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="no-data">{t('common:noData')}</div>}
        </div>
      </div>
    );
  };

  // ─── Вкладка: Активность ───────────────────────────────────────────

  const renderActivity = () => {
    if (!activityData) return null;
    const { top_users, daily_activity, actions_distribution, last_logins } = activityData;

    const pieData = actions_distribution.map((a, i) => ({
      name: ACTION_LABELS[a.action] || a.action_display,
      value: a.count,
      color: COLORS[i % COLORS.length],
    }));

    return (
      <div className="tab-content">
        <div className="filter-bar">
          <label>{t('analytics:activity.period')}:</label>
          <select value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>{t('analytics:activity.days7')}</option>
            <option value={30}>{t('analytics:activity.days30')}</option>
            <option value={90}>{t('analytics:activity.days90')}</option>
          </select>
        </div>

        <div className="charts-grid">
          {/* Активность по дням */}
          <div className="chart-card">
            <h3><TrendingUp size={18} /> {t('analytics:activity.activityByDay')}</h3>
            {daily_activity.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={daily_activity.map(d => ({
                  ...d,
                  date: new Date(d.date).toLocaleDateString(getDateLocale(), { day: '2-digit', month: '2-digit' })
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total_actions" stroke="#1e88e5" strokeWidth={2} name={t('analytics:activity.actions')} />
                  <Line type="monotone" dataKey="unique_users" stroke="#8b5cf6" strokeWidth={2} name={t('analytics:activity.usersCount')} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="no-data">{t('common:noData')}</div>}
          </div>

          {/* Распределение действий */}
          <div className="chart-card">
            <h3><Activity size={18} /> {t('analytics:activity.actionTypes')}</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="no-data">{t('common:noData')}</div>}
          </div>
        </div>

        {/* TOP пользователей */}
        {top_users.length > 0 && (
          <div className="section-card">
            <h3><Users size={18} /> {t('analytics:activity.mostActiveUsers')}</h3>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('analytics:activity.user')}</th>
                    <th>{t('analytics:activity.department')}</th>
                    <th>{t('analytics:activity.totalActions')}</th>
                    <th>{t('analytics:activity.creates')}</th>
                    <th>{t('analytics:activity.updates')}</th>
                    <th>{t('analytics:activity.views')}</th>
                    <th>{t('analytics:activity.logins')}</th>
                  </tr>
                </thead>
                <tbody>
                  {top_users.map(u => (
                    <tr key={u.user_id}>
                      <td><strong>{u.user_name}</strong></td>
                      <td>{u.department_name || '—'}</td>
                      <td><strong>{u.total_actions}</strong></td>
                      <td>{u.creates}</td>
                      <td>{u.updates}</td>
                      <td>{u.views}</td>
                      <td>{u.logins}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Последние входы */}
        {last_logins.length > 0 && (
          <div className="section-card">
            <h3><Clock size={18} /> {t('analytics:activity.lastLogins')}</h3>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('analytics:activity.user')}</th>
                    <th>{t('analytics:activity.department')}</th>
                    <th>{t('analytics:activity.ipAddress')}</th>
                    <th>{t('analytics:activity.loginTime')}</th>
                  </tr>
                </thead>
                <tbody>
                  {last_logins.map((l, i) => (
                    <tr key={i}>
                      <td>{l.user_name}</td>
                      <td>{l.department_name || '—'}</td>
                      <td><code>{l.ip_address || '—'}</code></td>
                      <td>{new Date(l.login_time).toLocaleString(getDateLocale())}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Вкладка: Дисциплина ───────────────────────────────────────────

  const renderDiscipline = () => {
    if (!disciplineData) return null;
    const { documents_discipline, plans_discipline } = disciplineData;
    const docSummary = documents_discipline.summary;
    const planSummary = plans_discipline.summary;

    // Данные для BarChart: on_time_rate документов и планов по отделам
    const chartData = documents_discipline.by_department
      .map(dept => {
        const planDept = plans_discipline.by_department.find(p => p.department_id === dept.department_id);
        return {
          name: shortenDeptName(dept.department_name),
          fullName: dept.department_name,
          docs_on_time: dept.on_time_rate,
          plans_on_time: planDept ? planDept.on_time_rate : 0,
        };
      })
      .filter(d => d.docs_on_time > 0 || d.plans_on_time > 0);

    return (
      <div className="tab-content">
        <div className="filter-bar">
          <label>{t('analytics:common.year')}:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            <button className="btn-export" onClick={async () => {
              try { const r = await api.exportDisciplineExcel({ year }); downloadBlob(r, `${t('analytics:export.disciplineFilename')}_${year}.xlsx`); } catch {}
            }}><Download size={14} /> {t('analytics:export.excel')}</button>
            <button className="btn-export" onClick={async () => {
              try { const r = await api.exportDisciplinePdf({ year }); downloadBlob(r, `${t('analytics:export.disciplineFilename')}_${year}.pdf`); } catch {}
            }}><Download size={14} /> {t('analytics:export.pdf')}</button>
          </div>
        </div>

        {/* Сводные карточки */}
        <div className="stats-grid">
          <div className="stat-card green">
            <CheckCircle size={24} />
            <div>
              <div className="stat-value">{docSummary.on_time_rate}%</div>
              <div className="stat-label">{t('analytics:discipline.documentsOnTime')}</div>
            </div>
          </div>
          <div className="stat-card red">
            <AlertTriangle size={24} />
            <div>
              <div className="stat-value">{docSummary.overdue_rate}%</div>
              <div className="stat-label">{t('analytics:discipline.documentsOverdue')}</div>
            </div>
          </div>
          <div className="stat-card green">
            <CheckCircle size={24} />
            <div>
              <div className="stat-value">{planSummary.on_time_rate}%</div>
              <div className="stat-label">{t('analytics:discipline.tasksOnTime')}</div>
            </div>
          </div>
          <div className="stat-card blue">
            <ClipboardList size={24} />
            <div>
              <div className="stat-value">{planSummary.avg_completion}%</div>
              <div className="stat-label">{t('analytics:discipline.avgPlanCompletion')}</div>
            </div>
          </div>
        </div>

        {/* BarChart */}
        {chartData.length > 0 && (
          <div className="chart-card">
            <h3><Shield size={18} /> {t('analytics:discipline.onTimeByDepartment')}</h3>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={chartData} margin={{ bottom: 100 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }} interval={0} height={120} />
                <YAxis domain={[0, 100]} />
                <Tooltip
                  formatter={(v) => `${v}%`}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                />
                <Legend />
                <Bar dataKey="docs_on_time" fill="#1e88e5" name={t('analytics:discipline.documentsLabel')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="plans_on_time" fill="#10b981" name={t('analytics:discipline.plansLabel')} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Таблица документов */}
        {documents_discipline.by_department.length > 0 && (
          <div className="section-card">
            <h3><FolderOpen size={18} /> {t('analytics:discipline.documentsDiscipline')}</h3>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('common:table.department')}</th>
                    <th>{t('analytics:discipline.total')}</th>
                    <th>{t('analytics:discipline.completed')}</th>
                    <th>{t('analytics:discipline.onTime')}</th>
                    <th>{t('analytics:discipline.late')}</th>
                    <th>{t('analytics:discipline.overdue')}</th>
                    <th>{t('analytics:discipline.onTimeRate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents_discipline.by_department.map(d => (
                    <tr key={d.department_id}>
                      <td>{d.department_name}</td>
                      <td>{d.total}</td>
                      <td>{d.completed}</td>
                      <td className="text-green">{d.completed_on_time}</td>
                      <td className="text-orange">{d.completed_late}</td>
                      <td className="text-red">{d.overdue}</td>
                      <td>
                        <div className="kpi-cell">
                          <div className="progress-bar-mini">
                            <div className="progress-fill" style={{
                              width: `${d.on_time_rate}%`,
                              background: d.on_time_rate >= 80 ? '#10b981' : d.on_time_rate >= 50 ? '#f59e0b' : '#ef4444'
                            }} />
                          </div>
                          <span>{d.on_time_rate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Таблица планов */}
        {plans_discipline.by_department.length > 0 && (
          <div className="section-card">
            <h3><ClipboardList size={18} /> {t('analytics:discipline.plansDiscipline')}</h3>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('common:table.department')}</th>
                    <th>{t('analytics:discipline.total')}</th>
                    <th>{t('analytics:discipline.completed')}</th>
                    <th>{t('analytics:discipline.onTime')}</th>
                    <th>{t('analytics:discipline.overdue')}</th>
                    <th>{t('analytics:discipline.avgCompletion')}</th>
                    <th>{t('analytics:discipline.onTimeRate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {plans_discipline.by_department.map(d => (
                    <tr key={d.department_id}>
                      <td>{d.department_name}</td>
                      <td>{d.total}</td>
                      <td>{d.completed}</td>
                      <td className="text-green">{d.completed_on_time}</td>
                      <td className="text-red">{d.overdue}</td>
                      <td>{d.avg_completion}%</td>
                      <td>
                        <div className="kpi-cell">
                          <div className="progress-bar-mini">
                            <div className="progress-fill" style={{
                              width: `${d.on_time_rate}%`,
                              background: d.on_time_rate >= 80 ? '#10b981' : d.on_time_rate >= 50 ? '#f59e0b' : '#ef4444'
                            }} />
                          </div>
                          <span>{d.on_time_rate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Вкладка: Год к году ────────────────────────────────────────────

  const MONTH_NAMES = months;

  const renderChangeBadge = (value, suffix = '') => {
    if (value === null || value === undefined) return <span className="change-neutral">—</span>;
    const cls = value > 0 ? 'change-positive' : value < 0 ? 'change-negative' : 'change-neutral';
    const icon = value > 0 ? <ArrowUpRight size={14} /> : value < 0 ? <ArrowDownRight size={14} /> : null;
    return <span className={cls}>{icon}{value > 0 ? '+' : ''}{value}{suffix}</span>;
  };

  const renderYearComparison = () => {
    if (!yearCompData) return null;
    const { summary, departments, top_improved, top_declined, monthly_trends } = yearCompData;

    const handleReload = () => {
      loadTabData('year_comparison');
    };

    const reportsY1Key = t('analytics:yearComparison_chart.reportsYear', { year: yearCompYear1 });
    const reportsY2Key = t('analytics:yearComparison_chart.reportsYear', { year: yearCompYear2 });
    const documentsY1Key = t('analytics:yearComparison_chart.documentsYear', { year: yearCompYear1 });
    const documentsY2Key = t('analytics:yearComparison_chart.documentsYear', { year: yearCompYear2 });

    const trendChartData = monthly_trends.map(m => ({
      name: MONTH_NAMES[m.month - 1],
      [reportsY1Key]: m.year1_reports,
      [reportsY2Key]: m.year2_reports,
      [documentsY1Key]: m.year1_documents,
      [documentsY2Key]: m.year2_documents,
    }));

    return (
      <div className="tab-content">
        {/* Фильтр-бар */}
        <div className="filter-bar">
          <label>{t('analytics:yearComparison.year1')}:</label>
          <select value={yearCompYear1} onChange={e => setYearCompYear1(Number(e.target.value))}>
            {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <label>{t('analytics:yearComparison.year2')}:</label>
          <select value={yearCompYear2} onChange={e => setYearCompYear2(Number(e.target.value))}>
            {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn-export" onClick={handleReload}>{t('analytics:yearComparison.compare')}</button>
        </div>

        {/* Сводные карточки */}
        <div className="stats-grid">
          <div className="stat-card blue">
            <FileText size={24} />
            <div>
              <div className="stat-value">{summary.year2.total_reports}</div>
              <div className="stat-label">{t('analytics:overview.reports')} ({yearCompYear2})</div>
              <div className="stat-change">{renderChangeBadge(summary.change.reports_pct, '%')}</div>
            </div>
          </div>
          <div className="stat-card orange">
            <FolderOpen size={24} />
            <div>
              <div className="stat-value">{summary.year2.total_documents}</div>
              <div className="stat-label">{t('analytics:overview.documents')} ({yearCompYear2})</div>
              <div className="stat-change">{renderChangeBadge(summary.change.documents_pct, '%')}</div>
            </div>
          </div>
          <div className="stat-card green">
            <ClipboardList size={24} />
            <div>
              <div className="stat-value">{summary.year2.total_plans}</div>
              <div className="stat-label">{t('analytics:overview.plans')} ({yearCompYear2})</div>
              <div className="stat-change">{renderChangeBadge(summary.change.plans_pct, '%')}</div>
            </div>
          </div>
          <div className="stat-card purple">
            <Award size={24} />
            <div>
              <div className="stat-value">{summary.year2.avg_kpi}</div>
              <div className="stat-label">{t('analytics:rating.avgKPI')} ({yearCompYear2})</div>
              <div className="stat-change">{renderChangeBadge(summary.change.kpi_pct, '%')}</div>
            </div>
          </div>
        </div>

        {/* График наложения */}
        <div className="chart-card">
          <h3><TrendingUp size={18} /> {t('analytics:yearComparison.dynamicsByMonth')}: {yearCompYear1} {t('analytics:yearComparison.vs')} {yearCompYear2}</h3>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={trendChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey={reportsY1Key} stroke="#94a3b8" strokeDasharray="5 5" />
              <Line type="monotone" dataKey={reportsY2Key} stroke="#1e88e5" strokeWidth={2} />
              <Line type="monotone" dataKey={documentsY1Key} stroke="#d4d4d8" strokeDasharray="5 5" />
              <Line type="monotone" dataKey={documentsY2Key} stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Топ улучшившихся и ухудшившихся */}
        <div className="yc-top-grid">
          <div className="section-card">
            <h3><ArrowUpRight size={18} /> {t('analytics:yearComparison.topImproved')}</h3>
            <div className="yc-top-list">
              {top_improved.map((d, i) => (
                <div key={d.department_id} className="yc-top-item positive">
                  <span className="yc-top-rank">#{i + 1}</span>
                  <span className="yc-top-name">{d.department_name}</span>
                  <span className="yc-top-kpi">{d.year1_kpi} → {d.year2_kpi}</span>
                  {renderChangeBadge(d.kpi_change_pct, '%')}
                </div>
              ))}
              {top_improved.length === 0 && <div className="no-data">{t('common:noData')}</div>}
            </div>
          </div>
          <div className="section-card">
            <h3><ArrowDownRight size={18} /> {t('analytics:yearComparison.topDeclined')}</h3>
            <div className="yc-top-list">
              {top_declined.map((d, i) => (
                <div key={d.department_id} className="yc-top-item negative">
                  <span className="yc-top-rank">#{i + 1}</span>
                  <span className="yc-top-name">{d.department_name}</span>
                  <span className="yc-top-kpi">{d.year1_kpi} → {d.year2_kpi}</span>
                  {renderChangeBadge(d.kpi_change_pct, '%')}
                </div>
              ))}
              {top_declined.length === 0 && <div className="no-data">{t('common:noData')}</div>}
            </div>
          </div>
        </div>

        {/* Большая таблица по отделам */}
        <div className="section-card">
          <h3>{t('analytics:yearComparison.comparisonByDepartments')}</h3>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('common:table.department')}</th>
                  <th>KPI {yearCompYear1}</th>
                  <th>KPI {yearCompYear2}</th>
                  <th>{t('analytics:yearComparison.change')}</th>
                  <th>{t('analytics:yearComparison.reportsYear', { year: yearCompYear1 })}</th>
                  <th>{t('analytics:yearComparison.reportsYear', { year: yearCompYear2 })}</th>
                  <th>{t('analytics:yearComparison.plansCompletionYear', { year: yearCompYear1 })}</th>
                  <th>{t('analytics:yearComparison.plansCompletionYear', { year: yearCompYear2 })}</th>
                </tr>
              </thead>
              <tbody>
                {departments.map(d => (
                  <tr key={d.department_id}>
                    <td>{d.department_name}</td>
                    <td>{d.year1_kpi}</td>
                    <td>{d.year2_kpi}</td>
                    <td>{renderChangeBadge(d.kpi_change_pct, '%')}</td>
                    <td>{d.year1_reports.total} ({d.year1_reports.approved} {t('analytics:yearComparison_chart.approvedShort')})</td>
                    <td>{d.year2_reports.total} ({d.year2_reports.approved} {t('analytics:yearComparison_chart.approvedShort')})</td>
                    <td>{d.year1_plans.total} ({d.year1_plans.avg_completion}%)</td>
                    <td>{d.year2_plans.total} ({d.year2_plans.avg_completion}%)</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── Вкладка: Карта рисков ──────────────────────────────────────────

  const RISK_COLORS = {
    low: { bg: '#d1fae5', color: '#059669', label: t('analytics:riskMap.low') },
    medium: { bg: '#fef3c7', color: '#b45309', label: t('analytics:riskMap.medium') },
    high: { bg: '#fee2e2', color: '#991b1b', label: t('analytics:riskMap.high') },
    critical: { bg: '#f3e8ff', color: '#6b21a8', label: t('analytics:riskMap.critical') },
  };

  const renderRiskMap = () => {
    if (!riskMapData) return null;
    const { summary, departments, critical_departments } = riskMapData;

    return (
      <div className="tab-content">
        {/* Фильтр-бар */}
        <div className="filter-bar">
          <label>{t('analytics:common.year')}:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Сводные карточки по уровням */}
        <div className="stats-grid">
          <div className="stat-card green">
            <CheckCircle size={24} />
            <div>
              <div className="stat-value">{summary.low}</div>
              <div className="stat-label">{t('analytics:riskMap.low')}</div>
            </div>
          </div>
          <div className="stat-card orange">
            <AlertTriangle size={24} />
            <div>
              <div className="stat-value">{summary.medium}</div>
              <div className="stat-label">{t('analytics:riskMap.medium')}</div>
            </div>
          </div>
          <div className="stat-card red">
            <AlertTriangle size={24} />
            <div>
              <div className="stat-value">{summary.high}</div>
              <div className="stat-label">{t('analytics:riskMap.high')}</div>
            </div>
          </div>
          <div className="stat-card purple">
            <Shield size={24} />
            <div>
              <div className="stat-value">{summary.critical}</div>
              <div className="stat-label">{t('analytics:riskMap.critical')}</div>
            </div>
          </div>
        </div>

        {/* Тепловая карта */}
        <div className="section-card">
          <h3><Activity size={18} /> {t('analytics:riskMap.heatMap')} ({t('analytics:riskMap.avgRiskScore', { score: summary.avg_risk_score })})</h3>
          <div className="risk-legend">
            {Object.entries(RISK_COLORS).map(([key, val]) => (
              <span key={key} className="risk-legend-item">
                <span className="risk-legend-dot" style={{ background: val.bg, borderColor: val.color }}></span>
                {val.label}
              </span>
            ))}
          </div>
          <div className="risk-grid">
            {departments.map(d => {
              const rc = RISK_COLORS[d.risk_level];
              return (
                <div
                  key={d.department_id}
                  className={`risk-cell ${d.risk_level} ${selectedRiskDept === d.department_id ? 'selected' : ''}`}
                  style={{ background: rc.bg, color: rc.color }}
                  onClick={() => setSelectedRiskDept(
                    selectedRiskDept === d.department_id ? null : d.department_id
                  )}
                  title={`${d.department_name}: ${d.risk_score} ${t('analytics:riskMap.riskPoints')}`}
                >
                  <div className="risk-cell-code">{d.department_code}</div>
                  <div className="risk-cell-score">{d.risk_score}</div>
                </div>
              );
            })}
          </div>

          {/* Детали выбранного отдела */}
          {selectedRiskDept && (() => {
            const dept = departments.find(d => d.department_id === selectedRiskDept);
            if (!dept) return null;
            return (
              <div className="risk-detail-card" style={{ borderLeftColor: RISK_COLORS[dept.risk_level].color }}>
                <h4>{dept.department_name} — {RISK_COLORS[dept.risk_level].label} ({dept.risk_score} {t('analytics:riskMap.riskPoints')})</h4>
                <div className="risk-detail-stats">
                  <span>{t('analytics:riskMap.kpi')}: <strong>{dept.kpi_score}</strong></span>
                  <span>{t('analytics:riskMap.overdue')}: <strong>{dept.overdue_tasks}</strong></span>
                  <span>{t('analytics:riskMap.completion')}: <strong>{dept.avg_completion}%</strong></span>
                  <span>{t('analytics:riskMap.reportsOnTimeRate')}: <strong>{dept.reports_on_time_rate}%</strong></span>
                  <span>{t('analytics:riskMap.carryOvers')}: <strong>{dept.carry_over_count}</strong></span>
                </div>
                {dept.risk_factors.length > 0 && (
                  <div className="risk-factors-list">
                    {dept.risk_factors.map((f, i) => (
                      <div key={i} className="risk-factor-item">
                        <span>{f.factor}</span>
                        <span className="risk-factor-points">+{f.points}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Критические отделы */}
        {critical_departments.length > 0 && (
          <div className="section-card">
            <h3><AlertTriangle size={18} /> {t('analytics:riskMap.departmentsRequiringAttention')} ({critical_departments.length})</h3>
            <div className="risk-critical-list">
              {critical_departments.map(d => (
                <div key={d.department_id} className="risk-critical-item" style={{ borderLeftColor: RISK_COLORS[d.risk_level].color }}>
                  <div className="risk-critical-header">
                    <span className="risk-critical-name">{d.department_name}</span>
                    <span className="risk-badge" style={{ background: RISK_COLORS[d.risk_level].bg, color: RISK_COLORS[d.risk_level].color }}>
                      {RISK_COLORS[d.risk_level].label} ({d.risk_score})
                    </span>
                  </div>
                  <div className="risk-critical-factors">
                    {d.risk_factors.map((f, i) => (
                      <span key={i} className="risk-factor-tag">{f.factor}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Полная таблица */}
        <div className="section-card">
          <h3>{t('analytics:riskMap.allDepartments')}</h3>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('common:table.department')}</th>
                  <th>{t('analytics:riskMap.level')}</th>
                  <th>{t('analytics:riskMap.score')}</th>
                  <th>{t('analytics:riskMap.kpi')}</th>
                  <th>{t('analytics:riskMap.overdue')}</th>
                  <th>{t('analytics:riskMap.completion')}</th>
                  <th>{t('analytics:riskMap.factors')}</th>
                </tr>
              </thead>
              <tbody>
                {departments.map(d => (
                  <tr key={d.department_id}>
                    <td>{d.department_name}</td>
                    <td>
                      <span className="risk-badge" style={{ background: RISK_COLORS[d.risk_level].bg, color: RISK_COLORS[d.risk_level].color }}>
                        {RISK_COLORS[d.risk_level].label}
                      </span>
                    </td>
                    <td><strong>{d.risk_score}</strong></td>
                    <td>{d.kpi_score}</td>
                    <td>{d.overdue_tasks}</td>
                    <td>{d.avg_completion}%</td>
                    <td>{d.risk_factors.map(f => f.factor).join('; ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── Вкладка: Рейтинг ────────────────────────────────────────────────

  const handleDeptClick = async (deptId) => {
    setRatingDeptId(deptId);
    try {
      const res = await api.getRatingHistory({
        department_id: deptId, period_type: ratingPeriodType, year,
      });
      setRatingHistory(res.data);
    } catch (err) {
      console.error('Ошибка загрузки истории:', err);
    }
  };

  const handleGenerateSnapshots = async () => {
    if (generatingSnapshots) return;
    setGeneratingSnapshots(true);
    try {
      await api.generateRatingSnapshots({ period_type: ratingPeriodType });
      loadTabData('rating');
    } catch (err) {
      console.error('Ошибка генерации:', err);
    }
    setGeneratingSnapshots(false);
  };

  const renderRating = () => {
    const rankBadgeClass = (rank) => {
      if (rank === 1) return 'rank-badge rank-1';
      if (rank === 2) return 'rank-badge rank-2';
      if (rank === 3) return 'rank-badge rank-3';
      return 'rank-badge';
    };

    const rankChangeDisplay = (change) => {
      if (change === null || change === undefined) return <span className="rank-change neutral">=</span>;
      if (change > 0) return <span className="rank-change positive">&#9650;{change}</span>;
      if (change < 0) return <span className="rank-change negative">&#9660;{Math.abs(change)}</span>;
      return <span className="rank-change neutral">=</span>;
    };

    return (
      <div className="tab-content rating-content">
        {/* Фильтр-бар */}
        <div className="filter-bar">
          <div className="filter-group">
            <label>{t('analytics:common.year')}:</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>{t('analytics:rating.periodLabel')}:</label>
            <select value={ratingPeriodType} onChange={(e) => { setRatingPeriodType(e.target.value); }}>
              <option value="weekly">{t('analytics:rating.weekly')}</option>
              <option value="monthly">{t('analytics:rating.monthly')}</option>
            </select>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleGenerateSnapshots}
            disabled={generatingSnapshots}
          >
            {generatingSnapshots ? t('analytics:rating.generating') : t('analytics:rating.generateSnapshots')}
          </button>
        </div>

        {/* Таблица рейтинга */}
        {ratingTableData && ratingTableData.latest_period ? (
          <div className="section">
            <h3>{t('analytics:rating.ratingFor', { period: ratingPeriodType === 'weekly' ? t('analytics:rating.weekFrom') : t('analytics:rating.monthFrom'), date: ratingTableData.latest_period })}</h3>
            <div className="table-responsive">
              <table className="data-table rating-table">
                <thead>
                  <tr>
                    <th>{t('analytics:rating.position')}</th>
                    <th>{t('analytics:rating.positionChange')}</th>
                    <th>{t('common:table.department')}</th>
                    <th>{t('analytics:rating.kpiScore')}</th>
                    <th>{t('analytics:rating.reports')}</th>
                    <th>{t('analytics:rating.documents')}</th>
                    <th>{t('analytics:rating.plans')}</th>
                    <th>{t('analytics:rating.penalty')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ratingTableData.departments.map(d => (
                    <tr
                      key={d.department_id}
                      className={`rating-row ${ratingDeptId === d.department_id ? 'selected' : ''}`}
                      onClick={() => handleDeptClick(d.department_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td><span className={rankBadgeClass(d.rank)}>{d.rank}</span></td>
                      <td>{rankChangeDisplay(d.rank_change)}</td>
                      <td>{d.department_name}</td>
                      <td>
                        <div className="kpi-cell">
                          <span className="kpi-value">{d.kpi_score}</span>
                          <div className="kpi-bar">
                            <div className="kpi-fill" style={{ width: `${Math.min(d.kpi_score, 100)}%` }}></div>
                          </div>
                        </div>
                      </td>
                      <td>{d.reports_score}</td>
                      <td>{d.documents_score}</td>
                      <td>{d.plans_score}</td>
                      <td>{d.overdue_penalty > 0 ? <span className="penalty">-{d.overdue_penalty}</span> : '0'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <Trophy size={48} />
            <p>{t('analytics:rating.noRatingDataDetailed', { year, periodType: ratingPeriodType === 'weekly' ? t('analytics:rating.weekly').toLowerCase() : t('analytics:rating.monthly').toLowerCase() })}</p>
            <p>{t('analytics:rating.clickGenerate')}</p>
          </div>
        )}

        {/* История рейтинга выбранного отдела */}
        {ratingHistory && (
          <div className="section rating-history-section">
            <h3>{t('analytics:rating.history')}: {ratingHistory.department.name}</h3>
            <div className="rating-dept-stats">
              <div className="stat-mini">
                <span className="stat-label">{t('analytics:rating.currentRank')}</span>
                <span className="stat-value">{ratingHistory.current_rank ?? '—'}</span>
              </div>
              <div className="stat-mini">
                <span className="stat-label">{t('analytics:rating.bestRank')}</span>
                <span className="stat-value positive">{ratingHistory.best_rank ?? '—'}</span>
              </div>
              <div className="stat-mini">
                <span className="stat-label">{t('analytics:rating.worstRank')}</span>
                <span className="stat-value negative">{ratingHistory.worst_rank ?? '—'}</span>
              </div>
              <div className="stat-mini">
                <span className="stat-label">{t('analytics:rating.avgKPI')}</span>
                <span className="stat-value">{ratingHistory.avg_kpi ?? '—'}</span>
              </div>
            </div>

            {ratingHistory.history.length > 0 ? (
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={ratingHistory.history}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period_start" tickFormatter={(v) => {
                      const d = new Date(v);
                      return `${d.getDate()}.${d.getMonth() + 1}`;
                    }} />
                    <YAxis yAxisId="kpi" domain={[0, 100]} />
                    <YAxis yAxisId="rank" orientation="right" reversed domain={[1, 30]} />
                    <Tooltip labelFormatter={(v) => `${t('common:table.period')}: ${v}`} />
                    <Legend />
                    <Line yAxisId="kpi" type="monotone" dataKey="kpi_score" name="KPI" stroke="#1e88e5" strokeWidth={2} />
                    <Line yAxisId="kpi" type="monotone" dataKey="reports_score" name={t('analytics:rating.reports')} stroke="#10b981" strokeWidth={1} strokeDasharray="5 5" />
                    <Line yAxisId="kpi" type="monotone" dataKey="documents_score" name={t('analytics:rating.documents')} stroke="#f59e0b" strokeWidth={1} strokeDasharray="5 5" />
                    <Line yAxisId="kpi" type="monotone" dataKey="plans_score" name={t('analytics:rating.plans')} stroke="#8b5cf6" strokeWidth={1} strokeDasharray="5 5" />
                    <Line yAxisId="rank" type="stepAfter" dataKey="rank" name={t('analytics:rating.position')} stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="no-data">{t('analytics:rating.noHistoricalData')}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Рендер активной вкладки ────────────────────────────────────────

  const renderTabContent = () => {
    if (loading) return <div className="loading">{t('analytics:common.loading')}</div>;
    switch (activeTab) {
      case 'overview':   return renderOverview();
      case 'kpi':        return renderKPI();
      case 'trends':     return renderTrends();
      case 'activity':   return renderActivity();
      case 'discipline': return renderDiscipline();
      case 'year_comparison': return renderYearComparison();
      case 'risk_map':   return renderRiskMap();
      case 'rating':     return renderRating();
      default:           return null;
    }
  };

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>{t('analytics:title')}</h1>
          <p>{t('analytics:subtitle')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-nav">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {renderTabContent()}
    </div>
  );
}

export default AnalyticsDashboard;
